import React, { useCallback, useMemo, useState, useRef } from 'react';
import { graphql, usePaginationFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import {
	IssueTypeEditViewWithIssueTypeConnection,
	type NullableOption,
	type Option,
} from '@atlassian/jira-issue-field-issue-type-editview-full';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous';
import type { issueTypeDropdown_nativeIssueTable$key as ProjectKey } from '@atlassian/jira-relay/src/__generated__/issueTypeDropdown_nativeIssueTable.graphql';
import issueTypeDropdown_SearchRefetchQuery, {
	type issueTypeDropdown_SearchRefetchQuery as IssueTypeRefetchQuery,
} from '@atlassian/jira-relay/src/__generated__/issueTypeDropdown_SearchRefetchQuery.graphql';
// eslint-disable-next-line jira/import/no-restricted-paths
import { useIssueCreateActions } from '../../../../../controllers/issue-create-form';
// eslint-disable-next-line jira/import/no-restricted-paths
import type { IssueCreateIssueType } from '../../../../../controllers/issue-create-form/types';
import messages from './messages';

const issueTypeToOption = (
	id: string,
	name: string,
	avatar: { small: string | null | undefined } | null | undefined,
): Option => ({
	label: name,
	value: id,
	avatar,
});

type Props = {
	/**
	 * The JiraProject to source issue types from. When `null` the issue type dropdown will be rendered in a disabled
	 * state. This is strictly to be shown as the fallback UI while project data is being fetched.
	 * */
	project: ProjectKey | null;
};

export const IssueTypeDropdown = ({ project }: Props) => {
	const { formatMessage } = useIntl();
	const { data, refetch } = usePaginationFragment<IssueTypeRefetchQuery, ProjectKey>(
		graphql`
			fragment issueTypeDropdown_nativeIssueTable on JiraProject
			@refetchable(queryName: "issueTypeDropdown_SearchRefetchQuery")
			@argumentDefinitions(
				first: { type: "Int", defaultValue: 1000 }
				after: { type: "String", defaultValue: null }
			) {
				id
				# TODO EM-7310 We should filter this by hierarchy level
				issueTypes(first: $first, after: $after) @connection(key: "issueTypeDropdown_issueTypes") {
					edges {
						node {
							id
							issueTypeId
							name
							avatar {
								small
							}
						}
					}
					...issueType_issueFieldIssueTypeEditviewFull_IssueTypeEditViewWithIssueTypeConnection_issueTypesFragmentRef
				}
			}
		`,
		project,
	);

	const isDisabled = project === null;

	const projectId = data?.id ?? '';
	const issueTypes = data?.issueTypes;
	const issueTypeIdMap = useMemo(
		(): Record<string, IssueCreateIssueType> =>
			issueTypes?.edges
				?.map((edge) => edge?.node)
				.filter(Boolean)
				.reduce((result: Record<string, IssueCreateIssueType>, node) => {
					// eslint-disable-next-line no-param-reassign
					result[node.id] = {
						id: node.id,
						issueTypeId: node.issueTypeId,
						name: node.name,
						avatar: node.avatar
							? {
									small: node.avatar.small,
								}
							: null,
					};
					return result;
				}, {}) ?? {},
		[issueTypes?.edges],
	);

	const { onChangeIssueType } = useIssueCreateActions();

	// TODO EM-7312 We should be sourcing default issue type from the API
	const defaultIssueType = issueTypes?.edges?.[0]?.node;
	const [value, setValue] = useState<NullableOption>(() =>
		defaultIssueType
			? issueTypeToOption(defaultIssueType.id, defaultIssueType.name, defaultIssueType.avatar)
			: null,
	);
	const defaultValue = useRef<NullableOption>(value);
	const prevIssueType = usePrevious(defaultValue.current);

	if (defaultValue.current && defaultValue.current !== prevIssueType) {
		// Update our form data with our default issue type
		const issueType = issueTypeIdMap[defaultValue.current.value];
		issueType && onChangeIssueType(issueType);
	}

	const handleChange = useCallback(
		(option: NullableOption) => {
			const issueType = option ? issueTypeIdMap[option.value] : null;
			if (issueType) {
				setValue(option);
				onChangeIssueType(issueType);
			}
		},
		[issueTypeIdMap, onChangeIssueType],
	);

	const onGetRefetchVariables = useCallback(() => ({ id: projectId }), [projectId]);

	return (
		<Box xcss={issueTypeDropdownStyles}>
			<IssueTypeEditViewWithIssueTypeConnection
				isDisabled={isDisabled}
				issueTypesFragmentRef={issueTypes ?? null}
				menuPosition="fixed"
				onChange={handleChange}
				onRefetch={refetch}
				onGetRefetchVariables={onGetRefetchVariables}
				refetchQuery={issueTypeDropdown_SearchRefetchQuery}
				value={value}
				spacing="compact"
				stopPropagationOfMenuEscape
				label={formatMessage(messages.dropdownLabel)}
			/>
		</Box>
	);
};

const issueTypeDropdownStyles = xcss({
	flexShrink: 0,
	width: '170px',
});
